import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DeliveryGuideForm from '../components/DeliveryGuideForm';
import useCurrentUser from '../hooks/useCurrentUser';
import { getPurchaseById, getDeliverySummaryForPurchase, createDeliveryGuide } from '../api';
import { hasPermission } from '../utils/permissions';
import InlineLoading from '../components/InlineLoading';
import { buildDeliveryGuideLineKey } from '../utils/deliveryGuideItems';

const DeliveryGuideCreatePage = () => {
  const { branchId, purchaseId } = useParams();
  const navigate = useNavigate();

  const [purchase, setPurchase] = useState(null);
  const [summary, setSummary] = useState([]);
  const [itemsToDeliver, setItemsToDeliver] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [driverName, setDriverName] = useState('');
  const [vehiclePlate, setVehiclePlate] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [notes, setNotes] = useState('');
  const { user } = useCurrentUser();
  const canCreateGuide = hasPermission(user, 'delivery_guide.create');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const resolvedBranchId = branchId || localStorage.getItem('selectedBranchId');
        const [purchaseData, summaryData] = await Promise.all([
          getPurchaseById(resolvedBranchId, purchaseId),
          getDeliverySummaryForPurchase(resolvedBranchId, purchaseId),
        ]);
        setPurchase(purchaseData);
        setSummary(summaryData.summary || []);
      } catch (e) {
        console.error('Error loading delivery guide data', e);
        setError('Error al cargar la guía de despacho.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [branchId, purchaseId]);

  const handleQuantityChange = (key, remainingQuantity, value) => {
    if (!canCreateGuide) return;
    const parsed = parseFloat(value.replace(',', '.'));
    if (Number.isNaN(parsed) || parsed < 0) {
      setItemsToDeliver((prev) => ({ ...prev, [key]: '' }));
      return;
    }
    if (parsed > remainingQuantity) {
      setItemsToDeliver((prev) => ({ ...prev, [key]: remainingQuantity }));
      return;
    }
    setItemsToDeliver((prev) => ({ ...prev, [key]: parsed }));
  };

  const handleSetMax = (key, remainingQuantity) => {
    if (!canCreateGuide) return;
    setItemsToDeliver((prev) => ({ ...prev, [key]: remainingQuantity }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canCreateGuide) return;
    if (!purchase) return;

    const payloadItems = summary
      .map((line) => {
        const key = buildDeliveryGuideLineKey(line);
        const qty = itemsToDeliver[key];
        if (!qty || qty <= 0) return null;
        return {
          varietyId: line.varietyId,
          varietyPriceId: line.varietyPriceId ?? null,
          priceIndex: Number.isInteger(line.priceIndex) ? line.priceIndex : null,
          metricUnit: line.metricUnit,
          varietyName: line.varietyName,
          deliveredQuantity: qty,
        };
      })
      .filter(Boolean);

    if (payloadItems.length === 0) {
      setError('Debe seleccionar al menos un producto con cantidad a despachar.');
      return;
    }

    try {
      setSaving(true);
      const resolvedBranchId = branchId || localStorage.getItem('selectedBranchId');
      await createDeliveryGuide(resolvedBranchId, {
        purchaseId: purchase.id,
        items: payloadItems,
        driverName: driverName || null,
        vehiclePlate: vehiclePlate || null,
        deliveryDate: deliveryDate || null,
        notes: notes || null,
      });
      navigate(`/branches/${resolvedBranchId}/purchases/${purchase.id}`);
    } catch (e) {
      console.error('Error creating delivery guide', e);
      const message = e.response?.data?.message || 'Error al crear la guía de despacho.';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <p className="p-4 text-center">
        <InlineLoading label="Cargando guía de despacho" />
      </p>
    );
  }

  if (!purchase) {
    return <p className="p-4 text-center text-red-600">No se encontró la compra para crear la guía.</p>;
  }

  const resolvedBranchId = branchId || localStorage.getItem('selectedBranchId');

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <div className="max-w-5xl mx-auto">
        <h1 className="text-2xl font-bold mb-4 text-center">Nueva Guía de Despacho</h1>

        {error && <p className="mb-4 text-center text-red-600">{error}</p>}

        <div className="bg-white rounded-lg shadow p-4 mb-4">
          <h2 className="text-lg font-semibold mb-2">Compra #{purchase.id}</h2>
          {purchase.Client && (
            <div className="text-sm text-gray-700">
              <p className="font-semibold">{purchase.Client.name}</p>
              {purchase.Client.rut && <p>RUT: {purchase.Client.rut}</p>}
              {(purchase.Client.address || purchase.Client.city) && (
                <p>
                  {purchase.Client.address}
                  {purchase.Client.city && `, ${purchase.Client.city}`}
                </p>
              )}
            </div>
          )}
        </div>

        <DeliveryGuideForm
          onSubmit={handleSubmit}
          summary={summary}
          itemsToDeliver={itemsToDeliver}
          canEdit={canCreateGuide}
          driverName={driverName}
          onDriverNameChange={(e) => setDriverName(e.target.value)}
          vehiclePlate={vehiclePlate}
          onVehiclePlateChange={(e) => setVehiclePlate(e.target.value)}
          deliveryDate={deliveryDate}
          onDeliveryDateChange={(e) => setDeliveryDate(e.target.value)}
          notes={notes}
          onNotesChange={(e) => setNotes(e.target.value)}
          itemsTitle="Productos a despachar"
          emptyMessage="No hay productos pendientes para esta compra."
          labels={{
            vehiclePlate: 'Patente del vehículo',
            currentGuide: 'En esta guía',
            setMaxAria: 'Usar máximo disponible para este producto',
          }}
          getMaxForLine={(line) => line.remainingQuantity || 0}
          getRemainingForLine={(line) => line.remainingQuantity || 0}
          onQuantityChange={handleQuantityChange}
          onSetMax={handleSetMax}
          footerLeft={
            <button
              type="button"
              onClick={() =>
                navigate(`/branches/${resolvedBranchId}/purchases/${purchase.id}`)
              }
              className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
            >
              Cancelar
            </button>
          }
          footerRight={
            canCreateGuide ? (
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
              >
                {saving ? <InlineLoading label="Guardando" /> : 'Crear guía de despacho'}
              </button>
            ) : null
          }
        />
      </div>
    </div>
  );
};

export default DeliveryGuideCreatePage;
